import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { getStrategies, deleteStrategy, explainStrategy } from '../api';
import { useAuth } from '../context/AuthContext';
import Spinner from '../components/Spinner';
import AlgovexInline from '../components/AlgovexInline';
import ExportStrategyModal from '../components/ExportStrategyModal';
import { track, Events } from '../analytics';

export default function StrategyList({ onEdit, onBacktest, onRequiresSignup }) {
  const { isAuthenticated } = useAuth();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [explaining, setExplaining] = useState(null); // strategy id being explained
  const [explanation, setExplanation] = useState(null); // {id, text}
  const [exportStrategyId, setExportStrategyId] = useState(null); // strategy being exported

  const load = () => {
    setLoading(true);
    getStrategies()
      .then((res) => setStrategies(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this strategy?')) return;
    await deleteStrategy(id);
    load();
  };

  const handleExplain = async (id) => {
    // Toggle off if already showing this strategy's explanation
    if (explanation?.id === id) {
      setExplanation(null);
      return;
    }
    setExplaining(id);
    setExplanation(null);
    try {
      const resp = await explainStrategy(id);
      setExplanation({ id, text: resp.data.explanation });
    } catch (err) {
      if (err.response?.data?.requires_signup && onRequiresSignup) {
        onRequiresSignup(err.response.data.detail);
      } else {
        setExplanation({ id, text: '⚠️ ' + (err.response?.data?.detail || err.message) });
      }
    } finally {
      setExplaining(null);
    }
  };

  if (loading) return <div style={{ padding: 24, color: 'var(--muted)' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: 24 }}>
      <Helmet>
        <title>My Strategies — Algovex</title>
        <meta name="description" content="Browse, manage, and build your algorithmic trading strategies on Algovex." />
        <meta name="robots" content="noindex" />
      </Helmet>
      {strategies.length === 0 && (
        <p style={{ color: 'var(--muted)' }}>
          {isAuthenticated
            ? 'No strategies yet. Create one using the Strategy Builder.'
            : 'No default strategies available.'}
        </p>
      )}
      {strategies.map((s) => (
        <div key={s.id} style={{ marginBottom: 10 }}>
          <div className="strategy-card-row" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: 14,
            borderRadius: explanation?.id === s.id ? '8px 8px 0 0' : 8,
            border: '1px solid var(--border)',
            background: 'var(--card)',
          }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>{s.name}</span>
                {s.is_complete === false && (
                  <span
                    title={(s.incomplete_reasons || []).join(' ') || 'This strategy has no complete buy/sell path and will never trade.'}
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--red)',
                      border: '1px solid var(--red)',
                      borderRadius: 999,
                      padding: '1px 8px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    ⚠ Incomplete
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                {s.graph?.nodes?.length || 0} blocks · Updated {new Date(s.updated_at).toLocaleDateString()}
              </div>
            </div>
            <div className="strategy-card-btns" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8, flexShrink: 0 }}>
              {isAuthenticated && (
                <button
                  onClick={() => handleExplain(s.id)}
                  disabled={explaining === s.id}
                  style={btnStyle('var(--blue)')}
                >
                  {explaining === s.id ? <><Spinner size={11} color="var(--bg)" style={{ marginRight: 4 }} />Explaining…</> : <><AlgovexInline size={13} /> Explain</>}
                </button>
              )}
              {isAuthenticated && (
                <button
                  onClick={() => { track(Events.STRATEGY_EXPORT_OPENED, { source: 'list', strategy_id: s.id }); setExportStrategyId(s.id); }}
                  style={exportBtnStyle}
                  title="Export to backtrader / PineScript code"
                >⬇ Export</button>
              )}
              <button onClick={() => onEdit(s)} style={btnStyle('var(--teal)')}>
                {isAuthenticated ? (s.is_default ? 'Show' : 'Edit') : 'Show'}
              </button>
              <button onClick={() => onBacktest(s)} style={btnStyle('var(--green)')}>Backtest</button>
              {isAuthenticated && (
                <span title={s.is_default ? 'You cannot delete a default strategy' : undefined}>
                  <button
                    onClick={s.is_default ? undefined : () => handleDelete(s.id)}
                    disabled={s.is_default}
                    style={{ ...btnStyle('var(--red)'), opacity: s.is_default ? 0.4 : 1, cursor: s.is_default ? 'not-allowed' : 'pointer' }}
                  >Delete</button>
                </span>
              )}
            </div>
          </div>
          {explanation?.id === s.id && (
            <div style={{
              padding: '14px 16px',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              fontSize: 13,
              lineHeight: 1.7,
              color: 'var(--text)',
              whiteSpace: 'pre-wrap',
            }}>
              {explanation.text}
            </div>
          )}
        </div>
      ))}

      {exportStrategyId && (
        <ExportStrategyModal
          strategyId={exportStrategyId}
          onClose={() => setExportStrategyId(null)}
          onRequiresSignup={onRequiresSignup}
        />
      )}
    </div>
  );
}

const btnStyle = (bg) => ({
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 600,
  background: bg,
  color: 'var(--bg)',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

// Neutral/secondary action: outline style so it stays readable on the dark card
// background (a filled gray button rendered dark-on-dark).
const exportBtnStyle = {
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 600,
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
